import {
  Box,
  Image,
  Text,
  Button,
  TextArea,
  Column,
  Heading,
  Row,
  Input,
  FormControl,
  PresenceTransition,
  Divider,
  Slide,
  Alert,
  Stack,
} from "native-base";
import { Amplify, API, Logger } from "aws-amplify";
import awsconfig from './aws-exports';
import { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import TagManager from 'react-gtm-module';
import { ButtonGroup } from "@aws-amplify/ui-react";

Amplify.configure(awsconfig);

const MAX_DESCRIPTION_LENGTH = 500;

enum ProjectTypeEnum {
  TechnicalAssessment = 'technical_assessment',
  CustomDevelopment = 'custom_development',
  SoftwareMaintenance = 'software_maintenance'
}

enum FormStates {
  Form = 'form',
  Loader = 'loader',
  Result = 'result',
  ThankYou = 'thankyou', 
}

interface IFormInput {
  projectType: ProjectTypeEnum;
  projectDescription: string;
}

interface IFormLead {
  email: string;
  firstName: string;
  cleanClassSize: string;
  reason: string; 
  classification: {
    min_cost: string;
    max_cost: string;
    // Add other properties as needed
  };
}

interface Classification {
  title: string;
  copy_1: string;
  copy_2: string;
  billing_rate: number;
  fte: number;
  max_cost: number;
  min_cost: number;
  max_dur_hour: number;
  min_dur_hour: number;
  max_dur_weeks: number;
  min_dur_weeks: number;
}

declare global {
  interface Window {
    dataLayer: any[];
  }
}

const formatter = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 1, minimumFractionDigits: 1 });
const logger = new Logger('App');

const HEAD_WIDTH_COLUMN_RESPONSIVE = ['100vw', '100vw', '80vw'];
const WIDTH_COLUMN_RESPONSIVE = ['100vw', '100vw', '600'];
const WIDTH_IMAGE_RESPONSIVE = WIDTH_COLUMN_RESPONSIVE;
const HEIGHT_IMAGE_RESPONSIVE = ['50vw', '50vw', '450'];
const SMALL_WIDTH_COLUMN_RESPONSIVE = ['100vw', '100vw', '550'];
const SMALL_WIDTH_IMAGE_RESPONSIVE = SMALL_WIDTH_COLUMN_RESPONSIVE;
const SMALL_HEIGHT_IMAGE_RESPONSIVE = ['50vw', '50vw', '470'];
const MEDIUM_WIDTH_COLUMN_RESPONSIVE = ['100vw', '100vw', '570'];
const MEDIUM_WIDTH_IMAGE_RESPONSIVE = MEDIUM_WIDTH_COLUMN_RESPONSIVE;
const MEDIUM_HEIGHT_IMAGE_RESPONSIVE = ['50vw', '50vw', '380'];
const LARGE_WIDTH_COLUMN_RESPONSIVE = ['100vw', '100vw', '650'];
const LARGE_WIDTH_IMAGE_RESPONSIVE = LARGE_WIDTH_COLUMN_RESPONSIVE;
const LARGE_HEIGHT_IMAGE_RESPONSIVE = ['50vw', '50vw', '320'];
const MAIL_WIDTH_COLUMN_RESPONSIVE = ['50vw', '50vw', '300'];
const MAIL_WIDTH_IMAGE_RESPONSIVE = MAIL_WIDTH_COLUMN_RESPONSIVE;
const MAIL_HEIGHT_IMAGE_RESPONSIVE = ['25vw', '25vw', '250'];
const PADDING_X_COLUMN_RESPONSIVE = ['4', '4', '0'];

TagManager.initialize({
  gtmId: 'GTM-NM6JH3Z',
  events: {
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  }
});

function App() {
  const [projectSize, setProjectSize] = useState("");
  const [projectReason, setProjectReason] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [formError, setFormError] = useState('');
  const [classification, setClassification] = useState<Classification>();
  const { formState: { errors, isSubmitting, isValid }, handleSubmit, control, reset, getValues } = useForm<IFormInput>({
    defaultValues: {
      projectDescription: '',
    }
  });
  const { formState: { errors: leadErrors, isSubmitting: leadSubmitting}, handleSubmit: handleSubmitLead, control: leadControl, reset: leadReset } = useForm<IFormLead>({
    defaultValues: {
      email: '',
      firstName: '',
    }
  });
  const [currentFormState, setFormState] = useState(FormStates.Form);
  const [progress, setProgress] = useState(20);

  useEffect(() => {
    var interval: any;
    if (isSubmitting && isValid && projectDescription.trim().length >= 30) {
      setFormState(FormStates.Loader);
      interval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress === 80) return prevProgress;
          const newProgress = prevProgress + 5;
          return newProgress > 80 ? 20 : newProgress;
        });
      }, 2000);
    }
  
    return () => {
      clearInterval(interval);
    };
  }, [isSubmitting, isValid, projectDescription]);
  
  useEffect(() => {
    var interval: any;
    if (!isSubmitting && currentFormState !== FormStates.Form && currentFormState !== FormStates.Result && currentFormState !== FormStates.ThankYou) {
      clearInterval(interval);
      setFormState(FormStates.Loader);
      interval = setInterval(() => {
        setProgress((prevProgress) => {
          const newProgress = prevProgress + (prevProgress < 80 ? 80 - prevProgress : 10);
          return newProgress;
        });
      }, 2000);
  
      if (progress > 100) {
        clearInterval(interval);
        setFormState(FormStates.Result);
      }
    }
  
    return () => {
      clearInterval(interval);
    };
  }, [isSubmitting, progress, currentFormState]);

  const progressBar = useMemo(() => { 
    const p = progress >= 100 ? 100 : progress;
    return (<div
      style={{
        position: 'relative',
        width: '100%',
        height: '50px',
        backgroundColor: '#343844',
        borderRadius: '20px'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: `${p}%`,
          backgroundColor: '#F25F1B',
          borderRadius: '20px'
        }}
      ></div>
      <Text
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          marginTop: '-0.5em',
          marginLeft: '-0.5em',
          color: 'white',
          fontWeight: 'bold',
        }}
      >
        {p}%
      </Text>
    </div>
  )}, [progress]);

  const getProgressLabel = () => {
    if (progress <= 20) {
      return 'Researching...';
    } else if (progress <= 80) {
      return 'Analyzing...';
    } else {
      return 'Preparing...';
    }
  };

  const getSizeImage = (size: string) => {
    if (size === 'small') {
      return { 
        width: SMALL_WIDTH_IMAGE_RESPONSIVE,
        height: SMALL_HEIGHT_IMAGE_RESPONSIVE,
        image: require('./assets/small_sizing.png'),
      };
    } else if (size === 'medium') {
      return { 
        width: MEDIUM_WIDTH_IMAGE_RESPONSIVE,
        height: MEDIUM_HEIGHT_IMAGE_RESPONSIVE,
        image: require('./assets/medium_sizing.png'),
      };
    } else if (size === 'large') {
      return { 
        width: LARGE_WIDTH_IMAGE_RESPONSIVE,
        height: LARGE_HEIGHT_IMAGE_RESPONSIVE,
        image: require('./assets/large_sizing.png'),
      };
    } else {
      return { 
        width: LARGE_WIDTH_IMAGE_RESPONSIVE,
        height: LARGE_HEIGHT_IMAGE_RESPONSIVE,
        image: require('./assets/large_sizing.png'),
      };
    }
  };

  useEffect(() => {
    if (formError) {
      leadReset();
    }
    const timeout = setTimeout(() => {
      setFormError('');
    }, 10000);

    return () => {
      clearTimeout(timeout);
    }
  }, [reset, leadReset, formError])

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    setFormError('');
    if(data.projectDescription.trim().length >= 30) {
      try {
        const result = await API.post('classapi', '/classification', {
          body: {
            ...data,
          }
        });
        console.log(result);
        if (result.classification) {
          setClassification(result.classification);
          setProjectSize(result.size);
          setProjectReason(result.reason);
          /** as is for now */
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'conversion', {
              send_to: 'AW-11208950757/rJWoCKf8vKwYEOWH7OAp'
            });
          } else {
            console.log('window.gtag is not a function');
          }

          window.dataLayer.push({
                event: 'event-conversion'
          });
        } else {
          setFormError('An error occurred in classification, Please try again');
          window.scrollTo(0, 0);
        }
      } catch (e) {
        setFormError('An error occurred in classification, Please try again');
        logger.error('api error', e);
        window.scrollTo(0, 0);
      }
    } else {
      setFormError(`Thank you for your prompt! To provide a more accurate and helpful response, could you please provide a bit more context or details? This will help ensure that I can address your query effectively. AIs work best with clear and specific information. Thanks for your cooperation!`);
      window.scrollTo(0, 0);
    }
  }

  const onLeadSubmit: SubmitHandler<IFormLead> = async (data) => {
    setFormError('');
    if(projectDescription.trim().length >= 30) {
      try {
        const result = await API.post('sendemailapi', '/sendemail', {
          body: {
            ...data,
            reason: projectReason,
            cleanClass: projectSize,
            classification,
            projectDescription,
          }
        });
        console.log(result);
          /** as is for now */
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'lead-conversion', {
              send_to: 'AW-11208950757/rJWoCKf8vKwYEOWH7OAp'
            });
          } else {
            console.log('window.gtag is not a function');
          }

          window.dataLayer.push({
                event: 'event-hot-lead-conversion'
          });
        setFormState(FormStates.ThankYou)
      } catch (e) {
        setFormError('An error occurred in email, Please try again');
        logger.error('api error', e);
        window.scrollTo(0, 0);
      }
    } else {
      setFormError(`Thank you for your prompt! To provide a more accurate and helpful response, could you please provide a bit more context or details? This will help ensure that I can address your query effectively. AIs work best with clear and specific information. Thanks for your cooperation!`);
      window.scrollTo(0, 0);
    }
  }

const resetForm = () => {
  reset();
  leadReset();
  setFormError('');
  setClassification(undefined);
  setProjectSize("");
  setProjectReason("");
  setProgress(20);
  setFormState(FormStates.Form);
}

const snakeCaseToNormalText = (input: string) => {
  let words = input.split('_');
  let capitalizedWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1));
  return capitalizedWords.join(' ');
}

const mainHeader = () => (
  <Stack direction={{ base: 'column', xl: 'row'}} alignItems="center" justifyContent="center">
    <Column w={HEAD_WIDTH_COLUMN_RESPONSIVE}>
      <Heading fontSize="4rem" fontWeight="bold" mb="10">Intelligent Project Sizing Tool</Heading>
      <Text fontSize="1.3rem">
        <Text>Get an accurate timeline and estimate for your software development project with just a few clicks. This isn’t just a number on a page – </Text>
        <Text color="#E8A01E">it’s a dynamic tool that empowers you to make informed decisions, optimize resources, and achieve your objectives.</Text>
      </Text>
      <Text fontWeight="normal" mt="50">
        <Text fontSize="1.3rem" color="#E8A01E">Try it below! </Text>
        <Text fontSize="1.3rem"> It is a breeze</Text>
      </Text>
    </Column>
  </Stack>
)

  return (
    <Box
      bg="#052e4f"
      justifyContent="center"
    >
      <Slide in={formError !== ''} placement="top">
        <Alert justifyContent="center" status="error">
          <Alert.Icon />
          <Text color="error.600" fontWeight="medium" textAlign="center">{formError}</Text>
        </Alert>
      </Slide>
      <PresenceTransition 
        visible={currentFormState === FormStates.Form} 
        initial={{
          opacity: 0,
          translateY: -150,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          transition: {
            duration: 750
          }
        }}
      >  
        {currentFormState === FormStates.Form && (
          <>
            {mainHeader()}
            <Stack direction={{ base: 'column', xl: 'row'}} alignItems="center" justifyContent="center" space={{ base: '8', xl: '20'}}>
              <Column w={WIDTH_COLUMN_RESPONSIVE} px={PADDING_X_COLUMN_RESPONSIVE}>
                <Box minH="20"></Box>
                <Image alt="Tool Image" alignSelf="center" height={HEIGHT_IMAGE_RESPONSIVE} width={WIDTH_IMAGE_RESPONSIVE} source={require('./assets/tool_image.png')} />
                <Text fontSize="1.4rem" fontWeight="hairline">Estimate your project in minutes and we'll send a copy of your results in your email.</Text>
              </Column>
              <Column space="4" justifyContent="center" w={WIDTH_COLUMN_RESPONSIVE} px={PADDING_X_COLUMN_RESPONSIVE}>
                <Box minH="20" pt="10" pb="5">
                  <Heading fontSize="1.3rem" fontWeight="normal">What is Project Type? (Select one)</Heading>
                </Box>
                <Box height={HEIGHT_IMAGE_RESPONSIVE} w="100%">
                  <FormControl isRequired isDisabled={isSubmitting} isInvalid={!!errors.projectType}>
                    <Controller
                        name="projectType"
                        control={control}
                        rules={{ required: true }}
                        defaultValue={ProjectTypeEnum.TechnicalAssessment}
                        render={({ field: { onChange, value } }) => (
                          <ButtonGroup defaultValue="Technical Assessment"
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr', // equal width bnt
                            gap: '10px', // Adjust to inc/dec space between btn
                          }}
                          >
                            {Object.values(ProjectTypeEnum).map((project) => (
                              <Button
                                key={project}
                                bgColor={value === project ? '#E8A01E' : 'white'}
                                onPress={() => onChange(project)}
                                height="120"
                                _hover={{ bg: '#E8A01E' }} 
                              >
                                <Text fontSize="1.0rem" 
                                  textAlign={'center'}
                                  color={'black'}
                                  fontWeight={600}
                                  style={{ fontFamily: 'Montserrat'}}
                                >
                                  {snakeCaseToNormalText(project).replace(/\s/g, '\n')}
                                </Text>
                              </Button>
                            ))}
                          </ButtonGroup>
                        )}
                      />
                      {errors.projectType && <FormControl.ErrorMessage>Please select Project Type</FormControl.ErrorMessage>}
                  </FormControl>
                  <Box py="10">
                    <Heading fontSize="1.3rem" fontWeight="normal">What is your Project about?</Heading>
                  </Box>
                  <FormControl isRequired isDisabled={isSubmitting} isInvalid={!!errors.projectDescription}>
                    
                    <Controller 
                      control={control}
                      name="projectDescription"
                      rules={{
                        required: true,
                        maxLength: MAX_DESCRIPTION_LENGTH,
                      }}
                      render={({ field }) => (
                        <>
                          <TextArea 
                            bgColor="white"
                            color="black" 
                            borderRadius="10"
                            minH="48"
                            my="1"
                            isInvalid={!!errors.projectDescription}
                            isDisabled={isSubmitting}
                            onChangeText={setProjectDescription}
                            {...field}
                          />
                          <Text textAlign='right'>{field.value.length} / {MAX_DESCRIPTION_LENGTH}</Text>
                        </>
                      )}
                    />
                    <FormControl.ErrorMessage position="absolute" bottom="0">Please enter Project Description - Max Length: {MAX_DESCRIPTION_LENGTH}</FormControl.ErrorMessage>
                  </FormControl>
                </Box>
                <Button 
                  bgColor="#F25F1B" 
                  onPress={handleSubmit(onSubmit)} 
                  isLoading={isSubmitting} 
                  isLoadingText="Submitting"
                  borderRadius="3xl"
                  px="18px"
                  py="20px"
                  width="full"
                  fontSize="1rem"
                >
                  <Text 
                    style={{ fontFamily: 'Montserrat'}} 
                    fontWeight={600}
                  >
                    Estimate your Project
                  </Text>
                </Button>
              </Column>
            </Stack>
          </>
        )}
      </PresenceTransition>

      <PresenceTransition
        visible={currentFormState === FormStates.Loader} 
        initial={{
          opacity: 0,
          translateY: 300,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          transition: {
            duration: 750
          }
        }}
      >
      {(currentFormState === FormStates.Loader) &&
        <>
          {mainHeader()}
          <Box width="30%" mx="auto" mt="100" justifyContent="center" height="300">
              {progressBar}
              <Text textAlign="center" fontSize="1.5rem" fontWeight="normal" pl="10">{getProgressLabel()}</Text>
          </Box>
        </>
      }
      </PresenceTransition>

      <PresenceTransition
        visible={currentFormState === FormStates.Result} 
        initial={{
          opacity: 0,
          translateY: 300,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          transition: {
            duration: 750
          }
        }}
      >
        {(currentFormState === FormStates.Result) && (
          <Column alignItems="center" alignSelf="center" w={WIDTH_COLUMN_RESPONSIVE} px={PADDING_X_COLUMN_RESPONSIVE}>
            <Heading fontSize="2.8rem" mb="12">Your Estimated Project Size</Heading>
            <Image alt={`Project Size Image: ${classification?.title}`} height={getSizeImage(projectSize?.toLowerCase()).height} width={getSizeImage(projectSize?.toLowerCase()).width} source={getSizeImage(projectSize?.toLowerCase() ?? '').image} />
            <Column space="8">
              <Heading textAlign="center" fontSize="2.2rem">{classification?.title}</Heading>
              <Text fontSize="0.9rem" fontWeight="thin" color="gray.400">(Disclaimer: Please note that the estimates generated by our tool are based on the information you have provided and various assumptions made by the tool. However, there may be multiple factors that could affect the final estimate, and a product specialist from our team will contact you to discuss your needs and provide a personalized quote.)</Text>
              <Text fontSize="1rem">{classification?.copy_1}</Text>
              <Divider />
              <Heading fontSize="1.2rem">Project Description</Heading>
              <Text>{getValues('projectDescription')}</Text>
              <Divider />
              <Text fontSize="0.9rem" fontWeight="thin">{projectReason}</Text>
              <Row space="20" alignItems="center" my="8">
                <Column space="8">
                  <Heading fontSize="1.1rem">Duration</Heading>
                  <Text fontSize="1rem" fontWeight="thin">{classification?.min_dur_weeks} - {classification?.max_dur_weeks} weeks</Text>
                </Column>
                <Column space="8">
                  <Heading fontSize="1.1rem">Pricing</Heading>
                  <Text fontSize="1rem" fontWeight="thin">{formatter.format((classification?.min_cost || 0) / 100000)}k - {formatter.format((classification?.max_cost || 0) / 100000)}k</Text>
                </Column>
              </Row>
              <Divider />
              <Text fontSize="1rem">{classification?.copy_2}</Text>
            </Column>

            <Column bgColor="white" borderTopWidth="32" borderTopColor="#F25F1B" py="10" px="16" space="8" mt="16">
              <Heading fontSize="1.7rem" textAlign="center" color="black" fontWeight="medium">Save Your Estimation</Heading>
              <Text fontSize="1.0rem" fontWeight="normal" color="black" textAlign="left">
                Get a detailed report of this estimation in your email.
              </Text>
              <Column space="4" justifyContent="center" w={WIDTH_COLUMN_RESPONSIVE} px={PADDING_X_COLUMN_RESPONSIVE}>
              <FormControl isRequired isDisabled={leadSubmitting} isInvalid={!!(leadErrors.firstName )}>
                <Text fontSize="1rem" fontWeight="normal" color="black">Name</Text>
                <Row space="2">
                  <Column flex="1">
                    <Controller
                      control={leadControl}
                      name="firstName"
                      rules={{
                        required: true
                      }}
                      render={({ field }) => (
                        <Input 
                          type="text" 
                          bgColor="white" 
                          color="black" 
                          height="12" 
                          borderRadius="10" 
                          my="1" 
                          {...field}
                        />
                      )}
                     />
                  </Column>
                </Row>
                <FormControl.ErrorMessage>Please enter your name</FormControl.ErrorMessage>
              </FormControl>
              <FormControl isRequired isDisabled={leadSubmitting} isInvalid={!!leadErrors.email}>
                  <Text fontSize="1rem" fontWeight="normal" color="black">Email</Text>
                  <Controller
                    control={leadControl}
                    name="email"
                    rules={{
                      required: true,
                      pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i
                    }}
                    render={({ field }) => (
                      <Input 
                        type="text" 
                        bgColor="white"
                        color="black" 
                        height="12" 
                        borderRadius="10" 
                        my="1"
                        {...field}
                      />
                    )}
                  />
                  <FormControl.ErrorMessage>Must enter valid email</FormControl.ErrorMessage>
              </FormControl>
              </Column>
              <Text fontSize="1rem" fontWeight="normal" color="black">
                Our team will also be in touch with you to discuss your project in more detail and answer any questions you may have.
              </Text>
              <Button
                bgColor="#F25F1B" 
                borderRadius="15"
                px="18px"
                py="20px"
                width="full"
                onPress={handleSubmitLead(onLeadSubmit)}
                isLoading={leadSubmitting}
              >
                <Text 
                  style={{ fontFamily: 'Montserrat'}}
                  fontWeight={600}
                >
                  Send Me the Estimation
                </Text>
              </Button>
            </Column>
          </Column>
        )}
      </PresenceTransition>
      <PresenceTransition
        visible={currentFormState === FormStates.ThankYou} 
        initial={{
          opacity: 0,
          translateY: 300,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          transition: {
            duration: 750
          }
        }}>
      {(currentFormState === FormStates.ThankYou) && ( <Column alignItems="center" justifyContent="center" alignSelf="center" w={WIDTH_COLUMN_RESPONSIVE} px={PADDING_X_COLUMN_RESPONSIVE}>
          <Image alt="Tool Image" alignSelf="center" height={MAIL_HEIGHT_IMAGE_RESPONSIVE} width={MAIL_WIDTH_IMAGE_RESPONSIVE} source={require('./assets/mailbox_image.png')} mt="100" mb="50" ml="70" />
          <Heading fontSize="2.3rem" textAlign="center">Thank you for using our</Heading>
          <Heading fontSize="2.3rem" pb="25" textAlign="center">Intelligent Sizing Tool!</Heading>
          <Text fontSize="1.3rem" textAlign="center">We have sent your project  estimation in your email. </Text>
          <Text fontSize="1.3rem" textAlign="center">Check your inbox for the project estimation.</Text>
          <Button
            bgColor="transparent" 
            borderRadius="15"
            borderWidth="1"
            borderColor="#ffffff"
            mt="50"
            mb="5"
            px="18px"
            py="20px"
            width="80"
            _hover={{ bg: '#F25F1B', borderColor: '#F25F1B' }}
            onPress={resetForm}               
          >
            <Text 
              style={{ fontFamily: 'Montserrat'}}
              fontWeight={600}
            >
              Estimate another Project
            </Text>
          </Button>
        </Column>
         )}
      </PresenceTransition>
    </Box>
  );
}

export default App;
